/**
 * The HUD — hearts, hunger, thirst.
 *
 * Kate: "every player gets 25 hearts... So 2 heart rows. So actually 24, 24." So the
 * hearts are drawn as two rows of twelve, which is where her own correction came from.
 * Half hearts matter: a tree bump costs 1.5.
 */
import { MAX_HEARTS, MAX_HUNGER, MAX_THIRST, type Survival } from '../player/survival'

const HEARTS_PER_ROW = MAX_HEARTS / 2

export class Hud {
  private readonly root: HTMLDivElement
  private readonly heartCells: HTMLSpanElement[] = []
  private readonly hungerCells: HTMLSpanElement[] = []
  private readonly thirstCells: HTMLSpanElement[] = []
  private readonly status: HTMLParagraphElement

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div')
    this.root.className = 'hud-bars'

    // Two rows of twelve, as Kate counted them.
    const hearts = document.createElement('div')
    hearts.className = 'hud-hearts'
    for (let row = 0; row < 2; row++) {
      const line = document.createElement('div')
      line.className = 'hud-row'
      for (let i = 0; i < HEARTS_PER_ROW; i++) {
        const cell = document.createElement('span')
        cell.className = 'hud-heart'
        line.append(cell)
        this.heartCells.push(cell)
      }
      hearts.append(line)
    }
    this.root.append(hearts)

    this.root.append(this.meter('hud-hunger', MAX_HUNGER, this.hungerCells))
    this.root.append(this.meter('hud-thirst', MAX_THIRST, this.thirstCells))

    this.status = document.createElement('p')
    this.status.className = 'hud-status'
    this.root.append(this.status)

    parent.append(this.root)
  }

  private meter(className: string, count: number, cells: HTMLSpanElement[]): HTMLElement {
    const row = document.createElement('div')
    row.className = `hud-row ${className}`
    for (let i = 0; i < count; i++) {
      const cell = document.createElement('span')
      cell.className = 'hud-pip'
      row.append(cell)
      cells.push(cell)
    }
    return row
  }

  update(survival: Survival, sheltered = false): void {
    for (let i = 0; i < this.heartCells.length; i++) {
      const filled = survival.hearts - i
      const cell = this.heartCells[i]!
      // Three states, because 1.5-heart tree damage lands on halves.
      cell.dataset.fill = filled >= 1 ? 'full' : filled >= 0.5 ? 'half' : 'empty'
    }
    for (let i = 0; i < this.hungerCells.length; i++) {
      this.hungerCells[i]!.dataset.fill = i < survival.hunger ? 'full' : 'empty'
    }
    for (let i = 0; i < this.thirstCells.length; i++) {
      this.thirstCells[i]!.dataset.fill = i < survival.thirst ? 'full' : 'empty'
    }

    this.status.textContent = this.statusText(survival, sheltered)
    this.status.dataset.urgent = survival.starvingFor !== null || survival.parchedFor !== null
      ? 'yes' : 'no'
  }

  private statusText(s: Survival, sheltered: boolean): string {
    if (s.dead) return `You died — ${s.deathCause}`
    if (s.asleep) return 'Asleep'
    const warnings: string[] = []
    // Being safe is worth saying out loud, or the burrow feels like it did nothing.
    if (sheltered) warnings.push('Sheltered — nothing can find you here')
    if (s.starvingFor !== null) warnings.push(`STARVING — eat within ${clock(s.starvingFor)}`)
    if (s.parchedFor !== null) warnings.push(`PARCHED — drink within ${clock(s.parchedFor)}`)
    return warnings.join('   ')
  }
}

function clock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds))
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`
}
